import { useState } from "react";
import { useLegalAcceptance } from "@/hooks/useLegalAcceptance";
import { FileText, Shield, Database, ArrowRight, Check, ExternalLink, Loader2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";

export function LegalAcceptanceModal() {
  const { acceptAll } = useLegalAcceptance();
  const [termsChecked, setTermsChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [dpaChecked, setDpaChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allChecked = termsChecked && privacyChecked && dpaChecked;
  const acceptedCount = [termsChecked, privacyChecked, dpaChecked].filter(Boolean).length;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await acceptAll();
    } catch (err) {
      setError("Ocorreu um erro ao registrar seu aceite. Tente novamente.");
      setIsSubmitting(false);
    }
  };

  const documents = [
    {
      id: "terms",
      label: "Termos de Uso",
      description: "Regras de uso, pagamentos e responsabilidades",
      checked: termsChecked,
      onChange: setTermsChecked,
      url: "/termos-de-uso",
      icon: FileText,
    },
    {
      id: "privacy",
      label: "Política de Privacidade",
      description: "Como seus dados são coletados e protegidos",
      checked: privacyChecked,
      onChange: setPrivacyChecked,
      url: "/privacidade",
      icon: Shield,
    },
    {
      id: "dpa",
      label: "Acordo de Processamento de Dados",
      description: "Acordo de processamento de dados (LGPD Art. 39)",
      checked: dpaChecked,
      onChange: setDpaChecked,
      url: "/dpa",
      icon: Database,
    },
  ];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-md px-4">
      <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-sidebar p-8 text-center">
          <img
            src="/brand/Solux-v7-800x200-2.png"
            alt="SoluxCRM"
            className="h-20 w-auto mx-auto object-contain"
          />
          <span className="inline-block mt-3 rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-white/90">
            Versão 1.0 — 14/04/2026
          </span>
          <h2 className="mt-4 text-2xl font-bold text-white">Antes de começar</h2>
          <p className="mt-2 text-sm leading-relaxed text-white/80">
            Leia e aceite os documentos abaixo para acessar o SoluxCRM.
            <br />É rápido e necessário apenas uma vez.
          </p>
        </div>

        {/* Document cards */}
        <div className="flex flex-col gap-3 p-6">
          {documents.map((doc) => {
            const Icon = doc.icon;
            return (
              <div
                key={doc.id}
                className={`rounded-2xl p-4 transition-all duration-200 cursor-pointer ${
                  doc.checked
                    ? "border-2 border-green-500 bg-green-50"
                    : "border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                }`}
                onClick={() => !isSubmitting && doc.onChange(!doc.checked)}
              >
                {/* Top row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Icon
                      className={`h-5 w-5 ${doc.checked ? "text-green-600" : "text-gray-400"}`}
                    />
                    <span className="text-sm font-semibold text-gray-800">{doc.label}</span>
                  </div>
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-700 hover:underline underline-offset-2"
                  >
                    Ver <ExternalLink className="h-3 w-3" />
                  </a>
                </div>

                {/* Description */}
                <p className="mt-1 ml-8 text-xs text-gray-500">{doc.description}</p>

                {/* Checkbox row */}
                <div className="mt-3 ml-8 flex items-center gap-2">
                  <div
                    className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition-colors ${
                      doc.checked
                        ? "border-green-500 bg-green-500"
                        : "border-gray-300 bg-white"
                    }`}
                  >
                    {doc.checked && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                  </div>
                  <span className="text-sm text-gray-700">Li e aceito este documento</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Progress */}
        <div className="px-6">
          <p className="mb-2 text-xs text-gray-500">
            {acceptedCount} de 3 documentos aceitos
          </p>
          <Progress value={(acceptedCount / 3) * 100} className="h-2 bg-gray-100 [&>div]:bg-green-500 [&>div]:transition-all [&>div]:duration-500" />
        </div>

        {/* Error */}
        {error && (
          <p className="mx-6 mt-3 rounded-lg bg-red-50 p-3 text-center text-sm text-red-600">
            {error}
          </p>
        )}

        {/* Button & footer */}
        <div className="px-6 pb-6 pt-4">
          <button
            onClick={handleConfirm}
            disabled={!allChecked || isSubmitting}
            className={`flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-base font-semibold transition-all duration-200 ${
              allChecked && !isSubmitting
                ? "bg-gradient-to-r from-green-600 to-green-700 text-white shadow-lg shadow-green-500/25 hover:from-green-700 hover:to-green-800 hover:shadow-xl hover:shadow-green-500/30 active:scale-[0.99]"
                : "cursor-not-allowed bg-gray-100 text-gray-400"
            }`}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                Confirmar e continuar
                <ArrowRight className="h-5 w-5" />
              </>
            )}
          </button>

          <div className="mt-4 text-center text-xs text-gray-400 space-y-0.5">
            <p>VERSARI SOLUÇÕES EM GESTÃO EMPRESARIAL LTDA</p>
            <p>CNPJ: 58.282.086/0001-72 — Várzea Grande - MT</p>
          </div>
        </div>
      </div>
    </div>
  );
}
